import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { Download, Share2, Receipt, ArrowLeft, AlertCircle, Loader2, ZoomIn, ZoomOut, ChevronLeft, ChevronRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { buildReceiptFilename, buildWhatsAppShare, buildPublicPdfUrl } from "@/components/receipts/ReceiptViewerModal";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/** Public receipt landing page — no authentication required. */
export default function ReceiptView() {
  const { token } = useParams();
  const [receipt, setReceipt] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [numPages, setNumPages] = useState(null);
  const [page, setPage] = useState(1);
  const [scale, setScale] = useState(window.innerWidth < 640 ? 0.65 : 1.0);
  const [pdfError, setPdfError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/public/receipts/${token}`)
      .then((r) => { if (!cancelled) setReceipt(r.data.receipt); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.detail || "Receipt not found"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <p className="text-slate-500 text-sm flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading receipt…
        </p>
      </div>
    );
  }

  if (error || !receipt) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl p-8 shadow-sm max-w-md w-full text-center">
          <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-slate-900">Receipt unavailable</h1>
          <p className="text-sm text-slate-500 mt-2">{error || "The link may be invalid or expired."}</p>
        </div>
      </div>
    );
  }

  const pdfUrl = buildPublicPdfUrl(receipt);
  const studioName = receipt?.snapshot?.studio?.name || "—";
  const orgName = receipt?.snapshot?.org?.display_name || receipt?.snapshot?.org?.organization_name || "";
  const summary = receipt?.snapshot?.payment_summary || {};
  const cur = receipt?.snapshot?.currency_symbol || "₹";
  const filename = buildReceiptFilename(receipt);
  const defaultCountryCode = receipt?.snapshot?.org?.default_country_code || "+91";

  const handleDownload = async () => {
    try {
      const res = await fetch(pdfUrl);
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error("Download failed. Please try again.");
    }
  };

  const handleShare = () => {
    const recipient = receipt.snapshot?.recipient || {};
    if (!recipient.whatsapp_phone && !recipient.phone) {
      toast.error("Customer mobile number unavailable.");
      return;
    }
    const { waUrl, phone } = buildWhatsAppShare({ receipt, defaultCountryCode });
    if (!phone) {
      toast.error("Customer mobile number unavailable.");
      return;
    }
    const win = window.open(waUrl, "_blank", "noopener,noreferrer");
    if (!win) {
      toast.message("Pop-up was blocked", {
        description: "Tap below to open WhatsApp.",
        action: { label: "Open WhatsApp", onClick: () => window.open(waUrl, "_blank", "noopener,noreferrer") },
      });
    }
  };

  const zoomIn = () => setScale((s) => Math.min(2.5, +(s + 0.2).toFixed(2)));
  const zoomOut = () => setScale((s) => Math.max(0.5, +(s - 0.2).toFixed(2)));

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-3 sm:px-4 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wide text-violet-600 font-semibold">Payment Receipt</p>
            <h1 className="text-base sm:text-lg font-bold text-slate-900 truncate" data-testid="receipt-number">
              {receipt.receipt_number}
            </h1>
            <p className="text-xs text-slate-500 truncate">
              {studioName}{orgName ? ` · ${orgName}` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={handleDownload}
              data-testid="public-receipt-download"
              className="h-9 px-3 inline-flex items-center gap-1 rounded-full bg-violet-600 text-white text-sm font-medium hover:bg-violet-700"
            >
              <Download className="w-4 h-4" /> <span className="hidden sm:inline">Download</span>
            </button>
            <button
              onClick={handleShare}
              data-testid="public-receipt-share"
              className="h-9 px-3 inline-flex items-center gap-1 rounded-full border border-violet-200 bg-white text-violet-700 text-sm font-medium hover:bg-violet-50"
            >
              <Share2 className="w-4 h-4" /> <span className="hidden sm:inline">Share</span>
            </button>
          </div>
        </div>
        {receipt.voided && (
          <div className="bg-red-50 border-y border-red-200 text-red-700 text-xs text-center py-1.5 font-semibold">
            This receipt has been voided.
          </div>
        )}
      </div>

      {/* Summary band */}
      <div className="max-w-3xl mx-auto px-3 sm:px-4 pt-4">
        <div className="grid grid-cols-3 gap-2 sm:gap-3 bg-white rounded-2xl border border-slate-200 p-3 sm:p-4">
          <Stat label="This Transaction" value={`${cur}${summary.transaction_amount ?? "—"}`} testId="rv-amount" />
          <Stat label="Cumulative Paid" value={`${cur}${summary.cumulative_paid ?? "—"}`} testId="rv-paid" />
          <Stat label="Outstanding" value={`${cur}${summary.outstanding_balance ?? 0}`} testId="rv-outstanding" />
        </div>
      </div>

      {/* Toolbar */}
      <div className="max-w-3xl mx-auto px-3 sm:px-4 mt-3">
        <div className="flex items-center justify-between gap-2 bg-white border border-slate-200 rounded-full px-2 py-1.5">
          <div className="flex items-center gap-1">
            <button onClick={zoomOut} className="h-8 w-8 grid place-items-center rounded-full hover:bg-slate-100" title="Zoom out">
              <ZoomOut className="w-4 h-4 text-slate-600" />
            </button>
            <span className="text-xs font-medium text-slate-600 w-10 text-center">{Math.round(scale * 100)}%</span>
            <button onClick={zoomIn} className="h-8 w-8 grid place-items-center rounded-full hover:bg-slate-100" title="Zoom in">
              <ZoomIn className="w-4 h-4 text-slate-600" />
            </button>
          </div>
          {numPages > 1 && (
            <div className="flex items-center gap-1">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="h-8 w-8 grid place-items-center rounded-full hover:bg-slate-100 disabled:opacity-40">
                <ChevronLeft className="w-4 h-4 text-slate-600" />
              </button>
              <span className="text-xs font-medium text-slate-600">{page} / {numPages}</span>
              <button onClick={() => setPage((p) => Math.min(numPages, p + 1))} disabled={page >= (numPages || 1)} className="h-8 w-8 grid place-items-center rounded-full hover:bg-slate-100 disabled:opacity-40">
                <ChevronRight className="w-4 h-4 text-slate-600" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* PDF Body */}
      <div className="max-w-3xl mx-auto px-3 sm:px-4 py-4">
        <div className="bg-white rounded-2xl border border-slate-200 overflow-auto p-3 sm:p-6 flex justify-center" data-testid="receipt-pdf-area" style={{ minHeight: "60vh" }}>
          {pdfError ? (
            <div className="text-center max-w-sm py-12">
              <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-2" />
              <p className="text-sm font-semibold text-slate-900">Unable to load receipt.</p>
              <p className="text-xs text-slate-500 mt-1">Please try downloading instead.</p>
              <button
                onClick={handleDownload}
                className="mt-3 h-9 px-4 inline-flex items-center gap-1 rounded-full bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium"
              >
                <Download className="w-4 h-4 mr-1" /> Download PDF
              </button>
            </div>
          ) : (
            <Document
              file={pdfUrl}
              onLoadSuccess={({ numPages: n }) => setNumPages(n)}
              onLoadError={(e) => { console.error("PDF load error", e); setPdfError(e); }}
              loading={
                <div className="text-slate-500 text-sm flex items-center gap-2 py-12">
                  <Loader2 className="w-4 h-4 animate-spin" /> Rendering PDF…
                </div>
              }
              error={
                <div className="text-center max-w-sm py-12">
                  <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-slate-900">Unable to load receipt.</p>
                  <p className="text-xs text-slate-500 mt-1">Please try downloading instead.</p>
                </div>
              }
            >
              <Page pageNumber={page} scale={scale} renderAnnotationLayer={false} renderTextLayer={false} className="shadow-lg" />
            </Document>
          )}
        </div>

        <a href="/" className="mt-4 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-violet-600">
          <ArrowLeft className="w-3 h-3" /> Visit dashboard
        </a>
      </div>
    </div>
  );
}

const Stat = ({ label, value, testId }) => (
  <div className="text-center" data-testid={testId}>
    <p className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">{label}</p>
    <p className="text-sm sm:text-base font-bold text-slate-900 mt-0.5 break-words">{value}</p>
  </div>
);
